/**
 * Reads for one analysis run.
 *
 * Split out of the hooks so the column lists can be exercised directly. The
 * failure worth guarding is silent: a column left out of a select makes the
 * matching mapper return null, and the band or modal it feeds simply does not
 * render. Nothing throws, nothing logs, and a mapper unit test built from a
 * hand-made row still passes. `__tests__/run-queries.test.ts` projects the mock
 * response down to the requested columns so that omission fails a test instead.
 *
 * A FAILED READ IS NOT AN EMPTY RESULT. Every read here throws on error rather
 * than resolving to null or [] — the same invariant the usage store learned the
 * hard way, where a 42501 was committed as "no usage" and wiped the counters.
 * Here the equivalent would render "no analysis has been generated" for a
 * permission error, which is indistinguishable from the truth and much worse.
 */

import { supabase } from '@/lib/supabase/client';
import type { AnalysisType } from './types';
import type { StrategicRunRow } from './status';

/**
 * The run row as PERSISTED — a superset of what the state resolver needs.
 * `report`, `derivation`, `research_coverage`, `provider_warnings` and
 * `input_snapshot` are all `unknown`: they are jsonb, and the mappers in
 * `report-view.ts` are what turn them into something renderable.
 */
export interface StoredStrategicRun extends StrategicRunRow {
  /** The report contract version this row was published under. */
  schema_version?: string | null;
  created_at?: string | null;
  completed_at?: string | null;
  methodology_version?: string | null;
  derivation?: unknown;
  research_coverage?: unknown;
  provider_warnings?: unknown;
  input_snapshot?: unknown;
}

/**
 * Every column the screen needs, in one read.
 *
 * `research_plan` is deliberately ABSENT. It holds the built search queries,
 * which nothing renders, and a report view has no reason to carry them.
 */
const RUN_COLUMNS = [
  'id',
  'status',
  'updated_at',
  'created_at',
  'completed_at',
  'failure_code',
  'input_fingerprint',
  'methodology_version',
  // WHICH CONTRACT THIS ROW WAS WRITTEN AGAINST. Without it the client had to
  // infer a shape from which fields happened to be present, which is how a
  // report from a newer deployment reads as a corrupt one — and how a genuinely
  // corrupt one reads as an old one.
  'schema_version',
  'report',
  'derivation',
  'research_coverage',
  'provider_warnings',
  'input_snapshot',
].join(', ');

const CLAIM_COLUMNS = [
  'id',
  'claim_key',
  'kind',
  'support_state',
  'material',
  'methodology_element',
  'statement',
  'counter_evidence_status',
  'detail',
].join(', ');

/** `source_id` is the join key; `claim_id` is what groups them. Both required. */
const CLAIM_SOURCE_COLUMNS = ['claim_id', 'source_id', 'role'].join(', ');

/**
 * `id` is needed to resolve a join, `evidence_id` is what the model cites, and
 * `license_policy` is what decides whether the excerpt and link may be shown at
 * all. Dropping the last one would withhold every excerpt rather than fail loudly.
 */
const SOURCE_COLUMNS = [
  'id',
  'evidence_id',
  'title',
  'publisher',
  'canonical_url',
  'published_at',
  'retrieved_at',
  'excerpt',
  'source_quality',
  'independence_group',
  'license_policy',
].join(', ');

/** The latest run for one analysis. Null means none has ever been requested. */
export async function fetchStrategicRunRow(
  startupId: string,
  analysisType: AnalysisType,
): Promise<StoredStrategicRun | null> {
  const { data, error } = await supabase
    .from('strategic_analysis_runs')
    .select(RUN_COLUMNS)
    .eq('startup_id', startupId)
    .eq('analysis_type', analysisType)
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error) throw error;
  return (data as StoredStrategicRun | null) ?? null;
}

/**
 * The newest run that actually published, whatever has happened since.
 *
 * Fetched alongside the latest run so a refresh does not blank the report:
 * while a replacement is researching, the previous one is still a true record of
 * what was assessed under the inputs it names, and blanking it makes a refresh
 * feel like a deletion. It is a separate query rather than a wider `limit`
 * because the last ready run can be arbitrarily far back — several failed or
 * timed-out attempts may sit on top of it.
 */
export async function fetchLastReadyRun(
  startupId: string,
  analysisType: AnalysisType,
): Promise<StoredStrategicRun | null> {
  const { data, error } = await supabase
    .from('strategic_analysis_runs')
    .select(RUN_COLUMNS)
    .eq('startup_id', startupId)
    .eq('analysis_type', analysisType)
    .eq('status', 'ready')
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error) throw error;
  return (data as StoredStrategicRun | null) ?? null;
}

/**
 * Just the statuses, for the profile entry card.
 *
 * Deliberately three columns: the card needs to know whether anything is ready,
 * in flight, or built from superseded inputs. Pulling `report` and
 * `input_snapshot` for five analyses to answer that would drag several hundred
 * kilobytes of jsonb into a profile page.
 */
export async function fetchStrategicRunStates(
  startupId: string,
): Promise<{ analysis_type: string; status: string; input_fingerprint: string }[]> {
  const { data, error } = await supabase
    .from('strategic_analysis_runs')
    .select('analysis_type, status, input_fingerprint')
    .eq('startup_id', startupId);

  if (error) throw error;
  return data ?? [];
}

export interface RunEvidence {
  claims: unknown[];
  claimSources: unknown[];
  sources: unknown[];
}

/**
 * The three sets a claim view is assembled from.
 *
 * Three reads rather than one embedded select: the claim/source foreign keys are
 * COMPOSITE — `(claim_id, run_id)` and `(source_id, run_id)` — and PostgREST
 * resource embedding across a composite FK is not dependable. Three indexed
 * reads bounded to one run is a cheaper thing to be sure about.
 *
 * Both `startup_id` and `run_id` bound every read. RLS already scopes them, but
 * an unbounded read that RLS happens to filter is one policy change away from
 * returning another company's rows.
 */
export async function fetchRunEvidence(
  startupId: string,
  runId: string,
): Promise<RunEvidence> {
  const [claims, claimSources, sources] = await Promise.all([
    supabase
      .from('strategic_analysis_claims')
      .select(CLAIM_COLUMNS)
      .eq('startup_id', startupId)
      .eq('run_id', runId),
    supabase
      .from('strategic_analysis_claim_sources')
      .select(CLAIM_SOURCE_COLUMNS)
      .eq('startup_id', startupId)
      .eq('run_id', runId),
    supabase
      .from('strategic_analysis_sources')
      .select(SOURCE_COLUMNS)
      .eq('startup_id', startupId)
      .eq('run_id', runId),
  ]);

  if (claims.error) throw claims.error;
  if (claimSources.error) throw claimSources.error;
  if (sources.error) throw sources.error;

  return {
    claims: claims.data ?? [],
    claimSources: claimSources.data ?? [],
    sources: sources.data ?? [],
  };
}

/**
 * The analysed company's display name.
 *
 * The header and the positioning map both name the company being analysed, and
 * neither can take that name from the route: a caller-supplied name would
 * caption someone else's report with a string an attacker chose. This reads the
 * profile row, so RLS decides whether the viewer may see it at all — an
 * investor without access gets nothing and the screen falls back to a generic
 * label rather than to a name it was handed.
 */
export async function fetchStartupName(startupId: string): Promise<string | null> {
  const { data, error } = await supabase
    .from('startup_profiles')
    .select('name')
    .eq('id', startupId)
    .maybeSingle();

  if (error) throw error;
  return data?.name ?? null;
}
